import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

export interface RosterEmployee {
  id: string;
  fullName: string;
  position: string;
  department: string;
}
export interface RosterShift {
  id: string;
  employeeId: string;
  date: string;
  startTime: string;
  endTime: string;
  note: string | null;
}
export interface RosterData {
  employees: RosterEmployee[];
  shifts: RosterShift[];
  leave: { employeeId: string; from: string; to: string }[];
}

export interface AttendanceRecord {
  id: string;
  employeeId: string;
  employee?: string;
  date: string;
  clockIn: string;
  clockOut: string | null;
  source: 'SELF' | 'MANAGER';
  note: string | null;
  rostered: { startTime: string; endTime: string } | null;
  workedMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  overtimeMinutes: number;
  overtimeApprovedMinutes: number;
  stillIn: boolean;
  stale: boolean;
}

export interface MyAttendance {
  linked: boolean;
  name?: string;
  today?: string;
  shiftToday?: { startTime: string; endTime: string; note: string | null } | null;
  open?: { clockIn: string; date: string } | null;
  recent?: AttendanceRecord[];
}

export interface ReportRow {
  employeeId: string;
  name: string;
  position: string;
  rostered: number;
  present: number;
  late: number;
  lateMinutes: number;
  absent: number;
  onLeave: number;
  notClockedOut: number;
  workedMinutes: number;
  overtimeMinutes: number;
  overtimeApprovedMinutes: number;
  earlyLeaves: number;
}

export const useRoster = (from: string, to: string) => useQuery({ queryKey: ['roster', from, to], enabled: !!from, queryFn: () => api<RosterData>(`/roster?from=${from}&to=${to}`) });
export const useAttendanceReport = (from: string, to: string, enabled: boolean) => useQuery({ queryKey: ['roster', 'report', from, to], enabled: enabled && !!from, queryFn: () => api<{ employees: ReportRow[] }>(`/attendance/report?from=${from}&to=${to}`).then((r) => r.employees) });
export const useAttendanceRecords = (from: string, to: string, enabled: boolean) => useQuery({ queryKey: ['roster', 'records', from, to], enabled: enabled && !!from, queryFn: () => api<{ records: AttendanceRecord[] }>(`/attendance?from=${from}&to=${to}`).then((r) => r.records) });
export const useMyAttendance = () => useQuery({ queryKey: ['roster', 'me'], queryFn: () => api<MyAttendance>('/attendance/me') });
export const useMyRoster = () => useQuery({ queryKey: ['roster', 'mine'], queryFn: () => api<{ linked: boolean; shifts: { date: string; startTime: string; endTime: string; note: string | null }[] }>('/roster/me') });
export const useApprovedOvertime = (period: string | undefined) =>
  useQuery({ queryKey: ['roster', 'overtime', period], enabled: !!period, retry: false, queryFn: () => api<{ employees: { employeeId: string; approvedMinutes: number }[] }>(`/attendance/overtime?period=${period}`).then((r) => r.employees) });

export function useRosterMutation<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['roster'] });
    },
  });
}

export const hours = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${m} min`;
};
